import { useState } from "react";
import {
  Title, Group, Button, TextInput, Table, ActionIcon, Stack, Text, Card,
  Menu,
} from "@mantine/core";
import { IconPlus, IconSearch, IconDots, IconPencil, IconTrash, IconHistory, IconEye } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import type { Bedrohung } from "@iso27001/shared";
import { bedrohungenApi } from "../../api/bedrohungen.js";
import { BedrohungFormModal } from "./BedrohungFormModal.js";
import { AuditHistoryModal } from "../../components/AuditHistoryModal.js";
import { useAuth } from "../../auth/AuthContext.js";
import { useRecordDialog } from "../../components/use-record-dialog.js";
import { rowOpenProps, stopRowOpen, nowrap } from "../../components/list-table.js";

export function BedrohungenListPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const qc = useQueryClient();
  const { t } = useTranslation();

  const [q, setQ] = useState("");
  const [auditId, setAuditId] = useState<number | null>(null);
  const dialog = useRecordDialog<Bedrohung>();

  const list = useQuery({
    queryKey: ["bedrohungen", { q }],
    queryFn: () => bedrohungenApi.list({ q: q || undefined, pageSize: 200 }),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => bedrohungenApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bedrohungen"] });
      notifications.show({ message: t("bedrohungen.list.notify.deleted"), color: "teal" });
    },
  });

  const onDelete = (b: Bedrohung) => {
    modals.openConfirmModal({
      title: t("bedrohungen.list.confirmDelete.title"),
      children: <Text size="sm">{t("bedrohungen.list.confirmDelete.message", { name: b.name })}</Text>,
      labels: { confirm: t("common.action.delete"), cancel: t("common.action.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => removeMut.mutate(b.id),
    });
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{t("bedrohungen.list.title")}</Title>
        {canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => dialog.open(null)}>
            {t("bedrohungen.newThreat")}
          </Button>
        )}
      </Group>
      <Card withBorder padding="xs">
        <Group>
          <TextInput
            placeholder={t("common.search.placeholder")}
            leftSection={<IconSearch size={14} />}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <Text size="sm" c="dimmed">{list.data ? t("common.entriesCount", { count: list.data.total }) : ""}</Text>
        </Group>
      </Card>
      <Card withBorder padding={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={nowrap}>{t("common.field.code")}</Table.Th>
              <Table.Th>{t("common.field.name")}</Table.Th>
              <Table.Th>{t("common.field.description")}</Table.Th>
              <Table.Th style={{ width: 48 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.data?.items.map((b) => (
              <Table.Tr key={b.id} {...rowOpenProps(() => dialog.open(b))}>
                <Table.Td><Text ff="monospace" size="sm">{b.code}</Text></Table.Td>
                <Table.Td><Text size="sm">{b.name}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1} c="dimmed">{b.description ?? ""}</Text>
                </Table.Td>
                <Table.Td {...stopRowOpen}>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" aria-label={t("common.action.actions")}><IconDots size={16} /></ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {canEdit ? (
                        <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => dialog.open(b)}>
                          {t("common.action.edit")}
                        </Menu.Item>
                      ) : (
                        <Menu.Item leftSection={<IconEye size={14} />} onClick={() => dialog.open(b)}>
                          {t("common.action.open")}
                        </Menu.Item>
                      )}
                      <Menu.Item leftSection={<IconHistory size={14} />} onClick={() => setAuditId(b.id)}>
                        {t("common.action.history")}
                      </Menu.Item>
                      {canEdit && (
                        <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => onDelete(b)}>
                          {t("common.action.delete")}
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
            {list.data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text ta="center" c="dimmed" py="md">{t("bedrohungen.list.empty")}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>
      <BedrohungFormModal key={dialog.key} opened={dialog.opened} onClose={dialog.close} initial={dialog.record} />
      <AuditHistoryModal
        opened={auditId !== null}
        onClose={() => setAuditId(null)}
        queryKey={["bedrohungen", "audit", auditId]}
        fetchFn={() => bedrohungenApi.audit(auditId!)}
      />
    </Stack>
  );
}
